import React, { useState, useEffect, useRef, useContext, useMemo } from 'react'
import { injectIntl } from 'react-intl'

import { Dropdown, Loader } from 'semantic-ui-react'

import { UserContext, NotifyContext } from 'context'
import { Card, CardIcon, CardBody, CardActions } from '../Card'
import Filter from '../Filter'

import Label from '../Label'
import Paginator from '../Paginator'
import DeleteModal from './DeleteModal'
import EditModal from './EditModal'

import api from '../../../actions/api'
import doRequest from 'actions/doRequest'
import './UsersPage.css'

import { cardsOnPage, roles, authTypes, activeTypes, editableRoles } from 'constants/config.json'


function UsersPage(props) {
    const { intl } = props
    const { currentUser, logout } = useContext(UserContext)

    const [loading, setLoading] = useState(false)
    const [userList, setUserList] = useState([])
    const [pageCount, setPageCount] = useState(0)
    const [page, setPage] = useState(1)
    const abortRequest = useRef(null)
    const [sortBy, setSortBy] = useState('byRole')
    const [options, setOptions] = useState({})
    const [filter, setFilter] = useState({})
    const [isFilterActive, setIsFilterActive] = useState(false)

    const { pushNotify } = useContext(NotifyContext)

    const translates = useMemo(() => ({
        notFound: intl.formatMessage({ id: 'title.recordNotFound' }),
        active: intl.formatMessage({ id: 'title.status' }),
        role: intl.formatMessage({ id: 'user.role' }),
        login: intl.formatMessage({ id: 'user.login' }),
        authType: intl.formatMessage({ id: 'authType' }),
        firstName: intl.formatMessage({ id: 'user.firstName' }),
        byActive: intl.formatMessage({ id: 'sort.byStatus' }),
        byFirstName: intl.formatMessage({ id: 'sort.byName' }),
        byAuthType: intl.formatMessage({ id: 'sort.byAuthType' }),
        byLogin: intl.formatMessage({ id: 'sort.byLogin' }),
        byRole: intl.formatMessage({ id: 'sort.byRole' }),
        filter: intl.formatMessage({ id: 'filter.filter' })
    }), [intl.locale]) // eslint-disable-line react-hooks/exhaustive-deps

    const filterList = useMemo(() => {
        return [
            {
                title: translates.role,
                type: 'dropdown',
                name: 'role',
                options: roles.map(item => { return { key: item, text: intl.formatMessage({ id: `user.role.${item}` }), value: item } })
            },
            {
                title: translates.firstName,
                type: 'input',
                name: 'firstName'
            },
            {
                title: translates.login,
                type: 'dropdown',
                name: 'login',
                options: options.logins
            },
            {
                title: translates.authType,
                type: 'dropdown',
                name: 'authType',
                options: authTypes.map(item => { return { key: item, text: intl.formatMessage({ id: `authType.${item}` }), value: item } })
            },
            {
                title: translates.active,
                type: 'dropdown',
                name: 'active',
                options: activeTypes.map(item => ({ key: item, text: intl.formatMessage({ id: item === 1 ? 'user.active' : 'user.disable' }), value: item }))
            }
        ]
    }, [intl.locale, options], options.firstNames, options.logins) // eslint-disable-line react-hooks/exhaustive-deps

    const getOptions = () => {
        setLoading(true)
        let requests = []

        requests.push(doRequest(api.user.list(), pushNotify, logout).ready)

        Promise.all(requests).then(result => {
            const res = result.reduce((acc, data) => {
                if (data.users) {
                    return {
                        ...acc,
                        logins: data.users.map((user) => { return { key: user.id, value: user.id, text: user.login } })
                    };
                }
                return acc
            }, {})

            setOptions({ ...res })
        }).catch(console.error)
            .finally(() => setLoading(false))
    }

    const sortOptions = [
        { key: 1, text: translates.byRole, value: "byRole" },
        { key: 2, text: translates.byFirstName, value: "byFirstName" },
        { key: 3, text: translates.byLogin, value: "byLogin" },
        { key: 4, text: translates.byAuthType, value: "byAuthType" },
        { key: 5, text: translates.byActive, value: "byActive" }
    ]

    const loadUserListFromPage = (page = 1) => {
        setUserList([])
        setLoading(true)

        const offset = cardsOnPage * (page - 1), count = cardsOnPage

        const req = doRequest(api.user.list(offset, count, filter.role, filter.firstName, filter.login, filter.authType, filter.active, sortBy), pushNotify, logout)
        abortRequest.current = req.abort

        req.ready.then(({ users, count }) => {
            const pages = Math.ceil(count / cardsOnPage)

            setUserList(users)
            if (page > pages & pages > 0) {
                loadUserListFromPage(pages)
            } else {
                setPageCount(pages)
                setPage(page)
            }
        }).catch(console.error)
            .finally(() => {
                setLoading(false)
                abortRequest.current = null
            })
    }

    const refresh = () => {
        loadUserListFromPage(page)
    }

    useEffect(() => {
        refresh()

        return () => {
            if (abortRequest.current)
                abortRequest.current()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (loading) return
        loadUserListFromPage(1)
        const filteredFilterValues = Object.values(filter).filter(el => el !== '' && el.length !== 0)
        filteredFilterValues.length !== 0 ? setIsFilterActive(true) : setIsFilterActive(false)
    }, [sortBy, filter]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        getOptions()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="users">
            <div className="topPanel">
                <Filter filter={filter} disabled={loading} setFilter={e => { setFilter(e) }} filterList={filterList} isFilterActive={isFilterActive} className="modalMaxWidth" />
                <Dropdown options={sortOptions} selection icon="sort" value={sortBy} onChange={(e, { value }) => setSortBy(value)} />
            </div>
            <div className="userBottom"><EditModal type="add" onEditUsers={refresh} hide={loading} /></div>
            {
                loading ?
                    <Loader className={"page-loader"} active inline="centered" />
                    :
                    userList.length === 0 ?
                        <Label text={translates.notFound} />
                        :
                        userList.map(user =>
                            <Card key={user.id}>
                                <CardIcon name="user circle" />
                                <CardBody>
                                    <span className="userName">{user.firstName} {user.lastName} <span className="userLogin">({user.login})</span></span>
                                    <span className="userRole">{intl.formatMessage({ id: 'user.role.' + user.role })}</span>
                                </CardBody>
                                <CardActions>
                                    <EditModal id={user.id} type="edit" onEditUsers={refresh} />
                                    <DeleteModal user={user} onDeleteUser={refresh} disabled={user.login === currentUser.login || (user.firstName === currentUser.firstName && user.lastName === currentUser.lastName) || !editableRoles.includes(user.role)} />
                                </CardActions>
                            </Card>
                        )
            }
            <Paginator pageCount={pageCount} page={page} setPage={loadUserListFromPage} disabled={loading} />
        </div>
    )
}

export default injectIntl(UsersPage)
